import { Box, Slider, Stack, Typography } from '@mui/material';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import type { UnitProviderProps } from '/@/views/visit-reports/edit/components/field-observation-section/components/precipitation-graphic';

/** A reference yield called out on the track — the model's suggestion, last campaign, … */
export type EnvironmentSliderMarker = {
    /** Yield in the base unit (kg/ha), same scale as `min` / `max` / `value`. */
    value: number;
    label: string;
    color: string;
    /** 'annotations' only: length of the dashed leader line, in px. Stagger these so markers don't collide. */
    lineHeight?: number;
};

/**
 * How markers are drawn. `annotations` labels each one in place under the track and needs
 * horizontal room; `list` puts a dot on the track and moves the labels to a legend below,
 * which is what fits a phone.
 */
export type EnvironmentSliderMarkerVariant = 'annotations' | 'list';

const MARKER_WIDTH = 140;
/** Room under the leader line for the value + label, which can wrap to two lines each. */
const MARKER_LABELS_HEIGHT = 76;
const MARKER_DOT_SIZE = 10;

const EnvironmentSliderV2 = ({
    percentiles,
    min,
    max,
    value,
    onChange,
    onChangeCommitted,
    valueLabelDisplay = 'auto',
    unitProviderProps,
    disabled,
    markers = [],
    markersVariant = 'annotations',
}: {
    /** Five yield thresholds bounding the probability bands; any other length renders a flat rail. */
    percentiles: number[];
    min: number;
    max: number;
    value: number;
    onChange: (e: Event, value: number) => void;
    /** Fires once the drag ends — use it for anything expensive (requests, store writes). */
    onChangeCommitted?: (e: Event | React.SyntheticEvent, value: number) => void;
    valueLabelDisplay?: 'auto' | 'on';
    unitProviderProps?: UnitProviderProps;
    disabled?: boolean;
    markers?: EnvironmentSliderMarker[];
    markersVariant?: EnvironmentSliderMarkerVariant;
}) => {
    const units = useUnitProvider(unitProviderProps);

    const COLOR_PALETTE = {
        VeryLow: { color: '#F5F5F5', label: _('Muy bajo') },
        Medium: { color: '#FFC103', label: _('Medio') },
        High: { color: '#9AD15E', label: _('Alto') },
        VeryHigh: { color: '#006A28', label: _('Muy alto') },
    };

    // Create gradient stops based on percentiles
    const createGradientStops = () => {
        if (percentiles.length !== 5) {
            return `linear-gradient(to right, ${COLOR_PALETTE.VeryLow.color}, ${COLOR_PALETTE.VeryLow.color})`;
        }

        const [p0, p1, p2, p3, p4] = percentiles as [number, number, number, number, number];
        const totalRange = max - min;

        // Calculate relative positions (0 to 1)
        const pos0 = (p0 - min) / totalRange;
        const pos1 = (p1 - min) / totalRange;
        const pos2 = (p2 - min) / totalRange;
        const pos3 = (p3 - min) / totalRange;
        const pos4 = (p4 - min) / totalRange;

        return `linear-gradient(to right,
            ${COLOR_PALETTE.VeryLow.color} 0%,
            ${COLOR_PALETTE.VeryLow.color} ${pos0 * 100}%,
            ${COLOR_PALETTE.Medium.color} ${pos0 * 100}%,
            ${COLOR_PALETTE.High.color} ${pos1 * 100}%,
            ${COLOR_PALETTE.VeryHigh.color} ${pos2 * 100}%,
            ${COLOR_PALETTE.High.color} ${pos3 * 100}%,
            ${COLOR_PALETTE.Medium.color} ${pos4 * 100}%,
            ${COLOR_PALETTE.VeryLow.color} ${pos4 * 100}%,
            ${COLOR_PALETTE.VeryLow.color} 100%
        )`;
    };

    const visibleMarkers = markers.filter(
        (marker) => Number.isFinite(marker.value) && marker.value >= min && marker.value <= max
    );
    const asList = markersVariant === 'list';
    // Annotations are absolutely positioned over the track, so the row below has to reserve
    // the vertical space they overflow into. The list variant needs no such gap.
    const markersHeight =
        !asList && visibleMarkers.length
            ? Math.max(...visibleMarkers.map((marker) => marker.lineHeight ?? 40)) + MARKER_LABELS_HEIGHT
            : 0;

    const markerOffset = (marker: EnvironmentSliderMarker) => `${((marker.value - min) / (max - min)) * 100}%`;

    return (
        // A pinned value bubble floats ~40px above the track, so reserve room for it or it
        // collides with whatever sits above the slider.
        <Stack pt={valueLabelDisplay === 'on' ? 3 : 0}>
            <Stack direction='row' spacing={2} alignItems={'center'}>
                <Typography variant='caption' color='primary.mui.inactive'>
                    {units.toCornYield(min, 2).localeValue}
                </Typography>
                <Box position='relative' flexGrow={1}>
                    <Slider
                        value={value}
                        onChange={onChange}
                        onChangeCommitted={
                            onChangeCommitted && ((event, newValue) => onChangeCommitted(event, newValue as number))
                        }
                        min={min}
                        max={max}
                        step={100}
                        disabled={disabled}
                        track={false}
                        sx={{
                            '& .MuiSlider-rail': {
                                background: createGradientStops(),
                                height: 4,
                                borderRadius: 4,
                                opacity: 1,
                            },
                        }}
                        valueLabelDisplay={valueLabelDisplay}
                        valueLabelFormat={(value) => {
                            return units.toCornYield(value, 2).toString();
                        }}
                    />
                    {visibleMarkers.map((marker) =>
                        asList ? (
                            <Box
                                key={marker.label}
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: markerOffset(marker),
                                    transform: 'translate(-50%, -50%)',
                                    width: MARKER_DOT_SIZE,
                                    height: MARKER_DOT_SIZE,
                                    borderRadius: '50%',
                                    backgroundColor: marker.color,
                                    pointerEvents: 'none',
                                }}
                            />
                        ) : (
                            <Stack
                                key={marker.label}
                                alignItems='center'
                                spacing={0.25}
                                width={MARKER_WIDTH}
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: markerOffset(marker),
                                    transform: 'translateX(-50%)',
                                    pointerEvents: 'none',
                                }}
                            >
                                <Box
                                    height={marker.lineHeight ?? 40}
                                    sx={{ borderLeft: '1px dashed', borderColor: marker.color }}
                                />
                                <Typography
                                    variant='subtitle1'
                                    fontWeight={700}
                                    color={marker.color}
                                    whiteSpace='nowrap'
                                >
                                    {units.toCornYield(marker.value, 0).toString()}
                                </Typography>
                                <Typography variant='caption' color={marker.color} textAlign='center'>
                                    {marker.label}
                                </Typography>
                            </Stack>
                        )
                    )}
                </Box>
                <Typography variant='caption' color='primary.mui.inactive'>
                    {units.toCornYield(max, 2).localeValue}
                </Typography>
            </Stack>
            {markersHeight > 0 && <Box height={markersHeight} />}
            {asList && (
                <Stack spacing={0.5} pt={1}>
                    {visibleMarkers.map((marker) => (
                        <Stack key={marker.label} direction='row' spacing={0.5} alignItems='center'>
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: marker.color,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography variant='body2' color={marker.color} fontWeight={700}>
                                {units.toCornYield(marker.value, 0).toString()}
                            </Typography>
                            <Typography variant='body2' color={marker.color}>
                                {marker.label}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            )}
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap pt={asList ? 1 : 0}>
                <Typography variant='caption' color='primary.mui.inactive'>
                    {_('Probabilidad de ocurrencia')}
                </Typography>
                {Object.values(COLOR_PALETTE).map((item) => (
                    <Stack direction='row' spacing={0.5} key={item.label} alignItems={'center'}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color }} />
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {item.label}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};

export default EnvironmentSliderV2;
